import localizeStripePrice from '@app-studio-web/ui/helpers/localizeStripePrice'
import type { StripePrice } from '@app-studio-web/ui/types'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

function daysIn(stripePrice: StripePrice) {
  if (!stripePrice.recurring) return 1

  const { interval, interval_count: intervalCount } = stripePrice.recurring

  switch (interval) {
    case 'week':
      return intervalCount * 7
    case 'month':
      return intervalCount * 30
    case 'year':
      return intervalCount * 365
    default:
      return intervalCount
  }
}

/**
 * A chunky selectable plan row in the Duolingo style: raised on a 4px bottom
 * border, with the billed price on the left and the per-day price on the right.
 * Price formatting follows `PlanCard` so the numbers match the baseline page.
 */
export default function DuoPlanRow({
  bestOffer,
  currency,
  isSelected,
  onSelect,
  stripePriceFull,
  stripePriceIntro,
}: {
  bestOffer?: boolean
  currency: string
  isSelected: boolean
  onSelect: () => void
  stripePriceFull: StripePrice
  stripePriceIntro?: StripePrice
}) {
  const { t } = useTranslation()

  const stripePrice = stripePriceIntro ?? stripePriceFull
  const isOneTime = !stripePrice.recurring

  const planLabel = isOneTime
    ? t('pages.checkout.plans.oneTimePurchase')
    : `${stripePrice.recurring!.interval_count}-${t(
        `pages.checkout.plans.${stripePrice.recurring!.interval}`,
        { count: stripePrice.recurring!.interval_count },
      )}`

  return (
    <div className="relative pt-3">
      {bestOffer && (
        <span className="bg-duo-yellow text-duo-ink absolute top-0 left-4 z-10 rounded-full px-3 py-1 text-xs font-extrabold uppercase">
          {t('pages.checkout.plans.mostPopular')}
        </span>
      )}

      <button
        className={twMerge(
          'duo-option justify-between',
          isSelected && 'duo-option-selected',
        )}
        onClick={onSelect}
        type="button"
      >
        <span className="flex items-center gap-3">
          <span
            className={twMerge(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
              isSelected ? 'border-duo-primary' : 'border-duo-line-dark',
            )}
          >
            {isSelected && (
              <span className="bg-duo-primary animate-check-pop h-3 w-3 rounded-full" />
            )}
          </span>

          <span className="flex flex-col text-left">
            <span className="text-lg">{planLabel}</span>

            <span className="flex items-center gap-2 text-sm font-bold">
              {stripePriceIntro && (
                <span className="text-duo-line-dark line-through">
                  {localizeStripePrice({
                    stripePrice: stripePriceFull,
                    userCurrency: currency,
                  })}
                </span>
              )}

              <span className={isSelected ? '' : 'text-duo-muted'}>
                {localizeStripePrice({ stripePrice, userCurrency: currency })}
              </span>
            </span>
          </span>
        </span>

        {!isOneTime && (
          <span className="shrink-0 text-right">
            <span className="block text-2xl leading-none font-extrabold">
              {localizeStripePrice({
                fraction: daysIn(stripePrice),
                stripePrice,
                userCurrency: currency,
              })}
            </span>

            <span
              className={twMerge(
                'text-xs font-bold',
                isSelected ? '' : 'text-duo-muted',
              )}
            >
              {t('pages.checkout.plans.perDay')}
            </span>
          </span>
        )}
      </button>
    </div>
  )
}
